import { type Messages } from './fr';

// English translation of the portal. Same keys as `fr`, enforced by the `Messages` type.
// Visible texts: no em dash, no infrastructure provider name.

export const en: Messages = {
  appName: 'Beta Assur',
  tagline: 'Premium installment and reminder management',
  common: {
    loading: 'Loading…',
    retry: 'Retry',
    save: 'Save',
    saved: 'Saved',
    cancel: 'Cancel',
    confirm: 'Confirm',
    create: 'Create',
    edit: 'Edit',
    delete: 'Delete',
    close: 'Close',
    search: 'Search',
    filter: 'Filter',
    reset: 'Reset',
    all: 'All',
    none: 'None',
    yes: 'Yes',
    no: 'No',
    loadMore: 'Show more',
    empty: 'Nothing to display.',
    actions: 'Actions',
    back: 'Back',
    details: 'Details',
    required: 'Required field',
    forbidden: 'You do not have permission to access this page.',
    notFound: 'Page not found.',
    errorTitle: 'An error occurred',
    requestId: 'Request reference',
    from: 'From',
    to: 'To',
    language: 'Language',
    sovereign: 'Data hosted on sovereign infrastructure.',
  },
  nav: {
    label: 'Main navigation',
    dashboard: 'Dashboard',
    installments: 'Installments',
    portfolio: 'Policies and policyholders',
    scenarios: 'Scenarios and templates',
    worklist: 'Worklist',
    imports: 'Imports and exports',
    reports: 'Reports',
    cash: 'Cash desk',
    settings: 'Settings',
    logout: 'Sign out',
    skipToContent: 'Skip to content',
    openMenu: 'Open menu',
    collapse: 'Collapse menu',
    expand: 'Expand menu',
  },
  theme: {
    label: 'Theme',
    light: 'Light',
    dark: 'Dark',
    system: 'Match system',
  },
  account: {
    title: 'My account',
    display: 'Display',
    compactMenu: 'Compact sidebar (icons only)',
    savedOnAccount:
      'Your preferences are saved to your account and follow you on all your devices.',
    profile: 'Profile',
    name: 'Display name',
    role: 'Role',
    tenant: 'Agency',
    password: 'Password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmPassword: 'Confirm new password',
    passwordHint: 'At least 12 characters; a passphrase is safer.',
    changePassword: 'Change password',
    passwordChanged: 'Password changed. Your other sessions have been signed out.',
    mismatch: 'The two new passwords do not match.',
  },
  roles: {
    admin: 'Administrator',
    manager: 'Manager',
    agent: 'Agent',
    readonly: 'Read only',
  },
  auth: {
    sso: {
      or: 'or',
      button: 'Sign in with Beta Suite',
      tenantRequired: 'Enter the agency code first.',
      echec: 'Beta Suite sign-in failed. Try again or use your password.',
      mfa: 'Your account requires strong authentication: enable it on Beta Suite or sign in with your password and code.',
      indisponible: 'Beta Suite sign-in is not enabled for this agency.',
    },
    loginTitle: 'Sign in',
    tenantCode: 'Agency code',
    email: 'Email address',
    password: 'Password',
    totp: 'Verification code (6 digits)',
    totpHint: 'Enter the code shown in your authenticator app.',
    submit: 'Sign in',
    invalid: 'Invalid credentials.',
    locked: 'Too many attempts. Try again in a few minutes.',
    invitationTitle: 'Activate your account',
    invitationIntro: 'Choose your password to access the portal.',
    passwordConfirm: 'Confirm password',
    passwordMismatch: 'The two passwords do not match.',
    passwordPolicy: 'At least 12 characters. A passphrase is recommended.',
    activate: 'Activate account',
    activated: 'Account activated. You can now sign in.',
    goToLogin: 'Go to sign in',
    missingToken: 'Incomplete invitation link.',
  },
  status: {
    upcoming: 'Upcoming',
    due: 'Due',
    overdue: 'Overdue',
    partial: 'Partial',
    paid: 'Paid',
    waived: 'Waived',
    cancelled: 'Cancelled',
  },
  contractStatus: {
    active: 'In force',
    suspended: 'Suspended',
    lapsed: 'Lapsed',
    cancelled: 'Cancelled',
    expired: 'Expired',
  },
  sequenceStatus: {
    scheduled: 'Scheduled',
    running: 'Running',
    paused: 'Paused',
    stopped_paid: 'Stopped (paid)',
    stopped_optout: 'Stopped (opt-out)',
    stopped_manual: 'Stopped',
    escalated: 'Escalated',
    completed: 'Completed',
  },
  channels: { whatsapp: 'WhatsApp', sms: 'SMS', email: 'Email' },
  paymentMethods: {
    kashy: 'Online payment',
    agency: 'At the agency',
    bank: 'Bank transfer',
    check: 'Check',
    other: 'Other',
  },
  languages: { fr: 'French', ar: 'Arabic' },
  periodicity: {
    annual: 'Annual',
    semiannual: 'Semi-annual',
    quarterly: 'Quarterly',
    monthly: 'Monthly',
    single: 'Single',
  },
  families: {
    auto: 'Motor',
    health: 'Health',
    life: 'Life and savings',
    home: 'Home',
    business: 'Commercial',
    other: 'Other',
  },
  dashboard: {
    alertLabels: {
      draft_mode: 'Draft mode is on: no message is sent to policyholders.',
      missing_phone: {
        one: '{n} policyholder without a reachable number.',
        other: '{n} policyholders without a reachable number.',
      },
      failed_messages: {
        one: '{n} undelivered message over 7 days.',
        other: '{n} undelivered messages over 7 days.',
      },
      import_failed: {
        one: '{n} failed import over 7 days.',
        other: '{n} failed imports over 7 days.',
      },
      sending_suspended:
        'Sending suspended after too many failures: check, then re-enable it in settings.',
      templates_rejected: {
        one: '{n} template rejected at WhatsApp approval.',
        other: '{n} templates rejected at WhatsApp approval.',
      },
      orphan_payments: { one: '{n} payment to allocate.', other: '{n} payments to allocate.' },
    },
    title: 'Dashboard',
    dueNext30: 'Outstanding to collect (30 days)',
    recovered30: 'Collected (30 days)',
    paidBeforeDueRate: 'Paid on time (90 days)',
    overdueOver15: 'Unpaid for more than 15 days',
    activeSequences: 'Active sequences',
    messagesThisMonth: 'Messages this month',
    messagesCost: 'cost',
    dueToday: 'Due today',
    openEscalations: 'Open escalations',
    lastPayments: 'Latest payments',
    alerts: 'Alerts',
    noAlert: 'No alerts.',
    draftBanner: 'Draft mode: reminders are simulated, no message is sent.',
    chart: 'Due and recovered per week (12 weeks)',
    due: 'Due',
    recovered: 'Recovered',
    weekOf: 'Week of',
  },
  installments: {
    title: 'Installments',
    policy: 'Policy',
    policyholder: 'Policyholder',
    product: 'Product',
    dueDate: 'Due date',
    amountDue: 'Amount due',
    amountPaid: 'Paid',
    status: 'Status',
    sequence: 'Reminder',
    nextRun: 'Next send',
    agent: 'Agent',
    step: 'step',
    minAmount: 'Min. amount (DT)',
    maxAmount: 'Max. amount (DT)',
    channel: 'Preferred channel',
    sequenceStatus: 'Reminder status',
    noPhone: 'No reachable number',
    highRisk: 'High risk of non-payment',
    risk: 'Risk',
    riskFactors: {
      late_history: 'often pays late',
      very_late_history: 'long delays',
      currently_overdue: 'overdue',
      no_history: 'no history',
      unreachable: 'unreachable',
      opted_out: 'opted out of messages',
      delivery_failures: 'undelivered messages',
    },
    insurer: 'Insurer',
    detailTitle: 'Installment',
    period: 'Coverage period',
    graceUntil: 'End of grace period',
    suspensionAt: 'Scheduled suspension',
    source: 'Source',
    sources: { computed: 'Computed', imported: 'Imported', api: 'API' },
    timeline: 'Timeline',
    noTimeline: 'No events for this installment.',
    timelineKinds: {
      sequence: 'Reminder',
      message: 'Message',
      payment: 'Payment',
      audit: 'Action',
    },
    openContract: 'View policy',
    openPolicyholder: 'View policyholder',
  },
  cash: {
    title: 'Cash journal',
    date: 'Entry date',
    mine: 'Your collections entered today.',
    team: 'Collections entered by the whole team today.',
    totals: 'Totals by payment method',
    total: 'Total collected',
    payments: { one: '{n} payment', other: '{n} payments' },
    none: 'No collections entered today.',
    time: 'Time',
    method: 'Method',
    reference: 'Reference',
    amount: 'Amount',
    print: 'Print',
  },
  mandate: {
    title: 'Direct debit',
    none: 'No direct debit: the policyholder can set one up from their page.',
    cap: 'Limit per debit',
    since: 'active since',
    cancel: 'Stop direct debit',
    cancelMessage: 'Upcoming installments will no longer be debited; reminders will resume.',
    installment: 'Installment',
    statuses: {
      pending: 'Awaiting signature',
      active: 'Active',
      cancelled: 'Stopped',
      failed: 'Rejected',
    },
    chargeStatuses: { pending: 'Processing', succeeded: 'Debited', failed: 'Rejected' },
  },
  claims: {
    title: 'Reported claims',
    none: 'No claims reported.',
    declare: 'Record a claim',
    kind: 'Type of loss',
    kinds: {
      accident: 'Accident',
      theft: 'Theft',
      fire: 'Fire',
      water_damage: 'Water damage',
      glass_breakage: 'Glass breakage',
      other: 'Other',
    },
    occurredOn: 'Date of loss',
    place: 'Location',
    description: 'Circumstances',
    noHealth: 'No health information: only the material circumstances.',
    thirdParty: 'A third party is involved',
    withThirdParty: 'with third party',
    save: 'Save',
    status: 'Follow-up',
    statuses: { received: 'Received', forwarded: 'Forwarded to the insurer', closed: 'Closed' },
  },
  renewal: {
    title: 'Renewal',
    none: 'No renewal offer.',
    premium: 'Proposed annual premium (DT)',
    validUntil: 'Response expected by',
    validUntilHint: 'By default, the policy expiry date.',
    propose: 'Offer renewal',
    invalid: 'Invalid premium: amount in dinars (e.g. 480,000).',
    cancel: 'Withdraw',
    cancelMessage: 'The policyholder will no longer be able to respond to this offer.',
    statuses: {
      pending: 'Pending',
      accepted: 'Accepted',
      declined: 'Declined',
      cancelled: 'Withdrawn',
    },
  },
  coverage: {
    title: 'Coverages',
    attestation: 'Payment certificate (PDF)',
    none: 'No detailed coverage for this policy.',
    code: 'Code',
    label: 'Label',
    sumInsured: 'Sum insured (DT)',
    premiumPart: 'Premium share (DT)',
    add: 'Add a coverage',
    invalid: 'Invalid entry: code, label and amounts in dinars (e.g. 1250,500).',
    noHealthData: 'Do not enter any health data: only the coverage name and its amounts.',
  },
  endorsements: {
    title: 'Endorsements',
    none: 'No endorsements.',
    currentPremium: 'Current annual premium',
    kind: 'Type',
    kinds: {
      premium_change: 'Premium change',
      coverage_change: 'Coverage change',
      object_change: 'Change of insured item',
      address_change: 'Change of address',
      other: 'Other',
    },
    effectiveDate: 'Effective date',
    delta: 'Annual premium change (DT)',
    deltaHint: 'Negative for a decrease (e.g. -50). Future installments are recalculated.',
    note: 'Comment',
    add: 'Save endorsement',
    saved: 'Endorsement saved, schedule recalculated.',
  },
  portfolio: {
    saveNotices: 'Save notice periods',
    notices: 'Notice periods',
    inheritNotice: 'Product line setting',
    title: 'Policies and policyholders',
    contracts: 'Policies',
    policyholders: 'Policyholders',
    searchContracts: 'Policy number',
    searchPolicyholders: 'Name, phone, ID number or email',
    searchHint: 'At least 2 characters. Phone, ID number and email: exact value.',
    policyNumber: 'Policy',
    status: 'Status',
    effectiveDate: 'Effective',
    expiryDate: 'Expiry',
    periodicity: 'Payment frequency',
    premium: 'Annual premium',
    insuredObject: 'Insured item',
    insurer: 'Insurer',
    product: 'Product',
    agent: 'Agent',
    noAgent: 'No agent',
    schedule: 'Installment schedule',
    statusHistory: 'Status history',
    changeStatus: 'Change status',
    newStatus: 'New status',
    reason: 'Reason',
    statusChanged: 'Status updated.',
    newContract: 'New policy',
    newPolicyholder: 'New policyholder',
    kind: 'Type',
    person: 'Individual',
    company: 'Company',
    firstName: 'First name',
    lastName: 'Last name',
    companyName: 'Company name',
    externalRef: 'System reference',
    phone: 'Phone',
    phoneVerified: 'verified',
    email: 'Email',
    cin: 'ID number',
    preferredChannel: 'Preferred channel',
    language: 'Message language',
    optOut: 'Message opt-out',
    optOutSince: 'since',
    noOptOut: 'No opt-out',
    contact: 'Contact and preferences',
    contractsOf: 'Policyholder’s policies',
    messages: 'Message log',
    noMessages: 'No messages.',
    sequenceNo: 'No.',
  },
  scenarios: {
    offsetFrom: 'Counted from',
    fromDue: 'the due date',
    fromNotice: 'the notice date',
    preferredFirst: 'Policyholder’s preferred channel first',
    title: 'Scenarios and templates',
    applyToRunning:
      'Also apply to running sequences (they resume at the equivalent step of the new version)',
    simulation: 'Portfolio simulation',
    simulate: 'Simulate',
    simInstallments: 'Installments affected',
    simMessages: 'Planned messages',
    simEscalations: 'Planned escalations',
    simNotStarted: 'Installments without a sequence',
    simCost: 'Estimated cost',
    simCalendar: 'Planned messages per day',
    scenarios: 'Scenarios',
    templates: 'Templates',
    installLibrary: 'Install the default library',
    libraryInstalled: 'Library installed',
    newScenario: 'New scenario',
    name: 'Name',
    family: 'Product family',
    anyFamily: 'All families',
    isDefault: 'Default',
    status: 'Status',
    statuses: { draft: 'Draft', published: 'Published', archived: 'Archived' },
    version: 'Version',
    published: 'published version',
    hasDraft: 'Draft in progress',
    steps: 'Steps',
    addMessage: 'Add a message',
    addEscalation: 'Add an escalation',
    offset: 'Day (relative to the due date)',
    kind: 'Type',
    kinds: { message: 'Message', escalation: 'Escalation' },
    channel: 'Channel',
    template: 'Template',
    fallback: 'Fallback',
    noFallback: 'No fallback',
    fallbackTemplate: 'Fallback template',
    label: 'Label',
    condition: 'Condition',
    noCondition: 'Always',
    conditionStatus: 'Only if the installment is',
    moveUp: 'Move up',
    moveDown: 'Move down',
    removeStep: 'Remove step',
    stopOnPayment: 'Stop on payment',
    pauseOnPromise: 'Pause on promise to pay (days)',
    publish: 'Publish',
    publishConfirm: 'Publish this version? Running sequences keep their version.',
    duplicate: 'Duplicate',
    archive: 'Archive',
    archiveConfirm: 'Archive this scenario?',
    preview: 'Timeline preview',
    previewDueDate: 'For an installment due on',
    plannedAt: 'Planned send',
    day: 'D',
    newTemplate: 'New template',
    body: 'Message text',
    variables: 'Available variables',
    renderPreview: 'Preview with sample data',
    missingVariables: 'Variables without a value',
    templateStatuses: {
      draft: 'Draft',
      pending: 'Under review',
      approved: 'Approved',
      rejected: 'Rejected',
    },
    templateName: 'Technical name (a-z, 0-9, _)',
    readOnlyTemplate: 'Template approved or under review: read only.',
  },
  imports: {
    title: 'Imports and exports',
    history: 'Import history',
    newImport: 'New import',
    kind: 'File type',
    kinds: {
      contracts: 'Policies and policyholders',
      installments: 'Installment schedules',
      payments: 'Payments',
    },
    file: 'CSV or XLSX file (5 MB max.)',
    upload: 'Upload',
    mapping: 'Column mapping',
    field: 'Beta Assur field',
    column: 'File column',
    ignore: 'Ignore',
    saveMapping: 'Remember for future imports',
    validate: 'Check the first 20 rows',
    missing: 'Required fields without a column',
    line: 'Row',
    errors: 'Errors',
    warnings: 'Warnings',
    ok: 'Valid',
    dryRun: 'Run the simulation',
    dryRunRequired: 'A simulation is required before the first real import.',
    run: 'Run the real import',
    runConfirm: 'Run the real import? The data in the file will be saved.',
    markAbsent: 'Cancel in-force policies missing from the file',
    progress: 'Progress',
    report: 'Download the error report',
    statuses: {
      uploaded: 'Uploaded',
      mapping: 'Mapping',
      validating: 'Checking',
      processing: 'Processing',
      completed: 'Completed',
      failed: 'Failed',
    },
    simulation: 'Simulation',
    real: 'Real',
    rows: 'Rows',
    rowsOk: 'valid',
    rowsError: 'with errors',
    rowsWarning: 'with warnings',
    summary: 'Result',
    createdAt: 'Created on',
    exportsSoon: 'Filtered exports are available from each list screen.',
  },
  notice: {
    presets: { 30: '1 month (30 days)', 60: '2 months (60 days)', 90: '3 months (90 days)' },
    custom: 'Custom',
    days: 'days',
  },
  subscription: {
    tab: 'Subscription',
    noPlan: 'No plan',
    legacy: 'Legacy agency: no limit and no feature removed. Contact Beta Suite to choose a plan.',
    statuses: {
      trial: 'Free trial',
      active: 'Active',
      past_due: 'Payment overdue',
      suspended: 'Suspended',
      cancelled: 'Cancelled',
    },
    trialEnds: (date: string) =>
      `Free trial until ${date}, in draft mode. Choose a plan to continue afterwards.`,
    trialConfirmed: (plan: string, date: string) =>
      `${plan} plan chosen: the subscription will start on ${date}, at the end of the trial.`,
    suspended:
      'Subscription suspended: real sending is stopped, your data remains available. Choose a plan to resume.',
    cancelled: 'Subscription cancelled: contact Beta Suite to resume it.',
    cancelScheduled: (date: string) => `Cancellation scheduled on ${date}.`,
    renewsOn: (date: string) => `Next renewal on ${date}.`,
    cycleLabel: 'Billing',
    cycles: { monthly: 'Monthly', annual: 'Annual' },
    nextCycle: (cycle: string) => `${cycle} from the next renewal`,
    messagesThisMonth: 'Messages sent this month (included)',
    limitNames: { users: 'Users', activeContracts: 'Policies in force', insurers: 'Insurers' },
    unlimited: 'unlimited',
    overageHint: 'Beyond the included messages, each message is billed at the plan rate.',
    plans: 'Plans',
    pricesHint: 'Prices excluding tax; the annual plan equals ten months.',
    perMonth: 'excl. tax / month',
    perYear: 'excl. tax / year',
    includedMessages: (n: number) => `${n.toLocaleString('en-GB')} messages included per month`,
    features: {
      public_api: 'Public API for your systems',
      webhooks: 'Outgoing webhooks',
      direct_debit: 'Direct debit',
      sso: 'Beta Suite single sign-on',
      sftp: 'IS integration (SFTP, connector)',
      llm_assist: 'Assisted reading of replies',
    },
    included: 'included',
    notIncluded: 'not included',
    current: 'Current plan',
    choose: 'Choose this plan',
    keep: 'Change billing cycle',
    confirmTitle: (plan: string) => `Switch to the ${plan} plan`,
    adjustmentDue: 'Adjustment added to the next statement:',
    adjustmentCredit: 'Credit deducted from the next statement:',
    effectiveOn: 'Effective on',
    blocked: 'Your usage exceeds the limits of this plan:',
    cancel: 'Cancel the subscription',
    cancelMessage:
      'The subscription will end at the end of the current period. You can undo the cancellation until then.',
    revertCancel: 'Undo cancellation',
    history: 'History',
    events: {
      created: 'Subscription created',
      confirmed: 'Plan chosen',
      started: 'Subscription started',
      renewed: 'Renewal',
      plan_changed: 'Plan change',
      cycle_changed: 'Billing cycle change',
      trial_expired: 'End of trial',
      cancel_requested: 'Cancellation requested',
      cancel_reverted: 'Cancellation undone',
      cancelled: 'Subscription cancelled',
      status_changed: 'Changed by Beta Suite',
    },
  },
  signup: {
    cta: 'Not a customer yet? Create your agency workspace',
    title: 'Create your agency workspace',
    intro:
      '30-day free trial, no commitment. Beta Suite reviews your request, then sends you the invitation by email.',
    agencyName: 'Agency name',
    agentCode: 'General agent code',
    optional: 'Optional',
    taxId: 'Tax identification number',
    taxIdHint: 'Example: 1234567A/M/000',
    city: 'City',
    insurers: 'Insurers represented',
    insurersHint: 'Separated by commas',
    plan: 'Preferred plan',
    plans: {
      ESSENTIEL: 'Essentiel: independent agent',
      PRO: 'Pro: structured agency',
      RESEAU: 'Réseau: large agency',
    },
    adminName: 'Your name (administrator)',
    phone: 'Phone',
    terms: 'I accept the terms of use and the processing of this information to open my workspace.',
    submit: 'Send the request',
    haveAccount: 'I already have an account',
    received:
      'Thank you, your request is recorded. You will receive the invitation by email once it is accepted.',
  },
  billing: {
    title: 'Invoices and payment',
    method: 'Payment method',
    transfer: 'Bank transfer',
    directDebit: 'Direct debit',
    mandatePending: 'mandate awaiting signature',
    transferHint:
      'Each invoice is payable by bank transfer within 10 days, quoting its number. Direct debit avoids missed payments.',
    startDirectDebit: 'Set up direct debit',
    stopDirectDebit: 'Stop direct debit',
    noInvoice: 'No invoice yet.',
    number: 'Invoice',
    period: 'Month',
    total: 'Total incl. tax',
    dueOn: 'Due date',
    status: 'Status',
    statuses: { open: 'To pay', paid: 'Paid', void: 'Void' },
    overdue: 'Overdue',
    download: 'Download',
    simulationTitle: 'Direct debit mandate signature (simulation)',
    simulationIntro:
      'Test environment: no account is debited. Choose the outcome of the signature.',
    sign: 'Sign the mandate',
    refuse: 'Refuse',
    banner: {
      past_due: 'A subscription invoice is overdue.',
      suspended:
        'Read-only account: subscription suspended, messages to policyholders are stopped.',
      cancelled: 'Subscription cancelled: read-only account.',
    },
    bannerAction: 'Settle now',
    bannerContact: 'Contact your agency administrator.',
  },
  calls: {
    tab: 'Today’s calls',
    title: 'Call the policyholder',
    noPhone: 'No reachable number: check the policyholder’s contact details.',
    remindersSent: (n: number) => `${n} reminder message(s) sent for this installment.`,
    payments: (list: string) => `Payments recorded: ${list}.`,
    noPayment: 'No payment recorded for this installment.',
    callBackAt: 'Call back scheduled on',
    outcome: 'Call outcome',
    outcomes: {
      reached: 'Reached',
      promise: 'Promise to pay',
      refused: 'Refuses to pay',
      no_answer: 'No answer',
      wrong_number: 'Wrong number',
      already_paid: 'Says already paid',
    },
    promiseDate: 'Payment promised on',
    callBack: 'Call back on',
    callBackHint: 'Empty: next working day if no answer, otherwise none.',
    report: 'Call report',
    save: 'Save the call',
  },
  pilotage: {
    title: 'Steering over 12 months',
    dueWithin: (days: number) => `Due within ${days} days`,
    installments: (n: number) => `${n} installment(s) to collect`,
    renewalRate: 'Renewal rate',
    renewalDetail: (yes: number, no: number) => `${yes} renewed, ${no} not renewed`,
    collectedMonth: 'Premiums collected this month',
    collectedYear: 'Since 1 January',
    lost: 'Lost policies',
    since: (date: string) => `Since ${date}`,
    byInsurer: 'Due within 90 days, by insurer',
    count: 'Installments',
    amount: 'Outstanding',
    lostRecent: 'Latest lost policies',
    noLost: 'No policy lost over the period.',
    reason: 'Reason',
  },
  settings: {
    notices: 'Notices and documents',
    dueNoticeDays: 'Notice before due date',
    dueNoticeHint:
      'Scenario steps counted from the notice date are sent this many days before each due date.',
    contractEndNoticeDays: 'Reminder before policy end',
    contractEndHint:
      'Message to the policyholder and a renewal case in the worklist (policies without tacit renewal).',
    noContractEndNotice: 'No reminder',
    attestationAfterPayment:
      'Send the payment certificate to the policyholder as soon as the current period is paid',
    attestationAfterPaymentShort: 'Certificate sent after payment',
    inherited: 'Agency setting',
    inheritedHint: 'In days; empty: agency setting.',
    title: 'Settings',
    tenant: 'Agency',
    insurers: 'Insurers',
    productLines: 'Product lines',
    agents: 'Agents',
    users: 'Users',
    security: 'My security',
    name: 'Name',
    timezone: 'Time zone',
    localeDefault: 'Default language',
    sendWindow: 'Sending window',
    sendDays: 'Sending days',
    dailyCap: 'Daily message limit',
    noCap: 'No limit',
    logo: 'Logo',
    uploadLogo: 'Upload (PNG or JPEG, 200 KB)',
    options: 'Options',
    optionLabels: {
      llmClassification:
        'Assisted reading of free-text replies: a language model suggests an intent (anonymized text, the manager decides)',
      monthlyReportEmail: 'Send the monthly collection report to administrators',
      allowInternationalPhones: 'Accept foreign phone numbers on import',
      assignEscalationsToAgents:
        'Assign escalations and calls to the policy’s agent rather than the team',
    },
    days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    sending: 'Sending mode',
    draftOn: 'Draft mode on: reminders are simulated.',
    draftOff: 'Live sending on: reminders are sent to policyholders.',
    activateSending: 'Enable live sending',
    activateConfirm: 'Type ACTIVER to confirm live sending of reminders.',
    backToDraft: 'Return to draft mode',
    holidays: 'Non-working days',
    holidayDate: 'Date',
    holidayLabel: 'Label',
    addHoliday: 'Add a day',
    holidayScopes: { national: 'National', tenant: 'Agency' },
    holidayKinds: { fixed: 'Fixed', estimated: 'Estimated', confirmed: 'Confirmed' },
    code: 'Code',
    legalName: 'Company name',
    displayName: 'Display name',
    senderName: 'SMS sender (11 characters)',
    legalMentions: 'Legal notice',
    labelFr: 'Label (FR)',
    labelAr: 'Label (AR)',
    family: 'Family',
    defaultPeriodicity: 'Default payment frequency',
    graceDays: 'Grace period (days)',
    suspensionDays: 'Suspension after (days)',
    noSuspension: 'Never',
    branch: 'Branch',
    phone: 'Phone',
    email: 'Email',
    linkedUser: 'Linked user',
    role: 'Role',
    status: 'Status',
    userStatuses: {
      invited: 'Invited',
      active: 'Active',
      locked: 'Locked',
      disabled: 'Disabled',
    },
    totp: 'TOTP',
    lastLogin: 'Last sign-in',
    invite: 'Invite a user',
    invitationLink: 'Invitation link to send to the invited person',
    disable: 'Disable',
    enable: 'Re-enable',
    disableConfirm: 'Disable this account? Its sessions will be closed.',
    totpTitle: 'Two-factor authentication (TOTP)',
    totpEnabled: 'Enabled',
    totpDisabled: 'Disabled',
    totpStart: 'Enable two-factor authentication',
    totpScan: 'Add this account to your authenticator app, then enter the code shown.',
    totpSecret: 'Secret key',
    totpCode: '6-digit code',
    totpConfirm: 'Confirm',
    totpDisable: 'Disable two-factor authentication',
  },
  worklist: {
    title: 'Worklist',
    mine: 'Mine',
    team: 'Team (unassigned)',
    all: 'All',
    closed: 'Closed',
    sortAge: 'Oldest first',
    sortAmount: 'Largest amounts',
    kind: 'Type',
    kinds: {
      unpaid: 'Unpaid',
      optout: 'Opt-out',
      payment_check: 'Payment to verify',
      question: 'Question',
      overpayment: 'Overpayment',
      refund: 'Refund',
      delivery: 'Delivery failed',
      renewal: 'Renewal',
      claim: 'Reported claim',
    },
    statuses: { open: 'Open', in_progress: 'In progress', closed: 'Closed' },
    openedAt: 'Opened on',
    remaining: 'Balance due',
    assigned: 'Assigned to',
    nobody: 'Team',
    me: 'me',
    empty: 'No escalations to handle.',
    inbound: 'Message from the policyholder',
    suggestion: 'Suggested reading (to be checked)',
    intents: {
      paid_elsewhere: 'Already paid',
      stop: 'Stop receiving messages',
      later: 'Requests more time',
      question: 'Question',
      other: 'Other',
    },
    detail: 'Details',
    notes: 'Handling history',
    messages: 'Latest messages sent',
    take: 'Take ownership',
    note: 'Add a note',
    noteLabel: 'Note',
    call: 'Log a call',
    markPaid: 'Mark as paid',
    grantDelay: 'Grant an extension',
    days: 'Days',
    cancelContract: 'Cancel the policy',
    cancelConfirm: 'Cancel this policy? Upcoming installments will be cancelled.',
    close: 'Close',
    resolution: 'Resolution',
    reassign: 'Reassign',
    actions: {
      take: 'Taken over',
      note: 'Note',
      call: 'Call',
      mark_paid: 'Paid',
      grant_delay: 'Extension granted',
      cancel_contract: 'Cancellation',
      close: 'Closure',
      assign: 'Reassignment',
    } as Record<string, string>,
    open: 'Open',
  },
  payments: {
    record: 'Record a payment',
    amount: 'Amount (DT)',
    method: 'Payment method',
    paidAt: 'Payment date',
    reference: 'Reference',
    issueReceipt: 'Send a Beta Assur receipt to the policyholder',
    recorded: 'Payment recorded.',
    overpaid: 'Overpayment flagged in the worklist',
    sendNow: 'Send the current step now',
    sendNowConfirm: 'Send the current reminder step right away (outside the schedule)?',
    sent: 'Send requested.',
    payLink: 'Policyholder page link',
    showPayLink: 'Show payment link',
  },
  bulk: {
    selected: { one: '{n} selected', other: '{n} selected' },
    selectAll: 'Select all',
    select: 'Select',
    action: 'Bulk action',
    actions: {
      start: 'Start reminder',
      pause: 'Pause',
      resume: 'Resume',
      stop: 'Stop reminder',
      change_scenario: 'Change scenario',
      mark_paid: 'Mark as paid',
    },
    scenario: 'Scenario',
    preview: 'View summary',
    recap: 'Summary',
    applicable: { one: '{n} installment affected', other: '{n} installments affected' },
    skipped: { one: '{n} skipped', other: '{n} skipped' },
    apply: 'Apply',
    applied: { one: '{n} installment processed', other: '{n} installments processed' },
  },
  rights: {
    title: 'Opt-out and rights',
    optOutChannels: 'Channels refused by the policyholder',
    saveOptOut: 'Save opt-out',
    verifyPhone: 'Verify the number',
    sendCode: 'Send a code by SMS',
    codeSent: 'Code sent. Enter the code provided by the policyholder.',
    code: 'Code received by the policyholder',
    confirmCode: 'Validate',
    verified: 'Number verified',
    export: 'Export data (JSON)',
    anonymize: 'Anonymize the policyholder',
    anonymizeConfirm:
      'Type ANONYMISER: identity and contact details will be permanently erased, the financial history is kept.',
    proof: 'Proof of sending (PDF)',
  },
  integrations: {
    title: 'Messaging and payment',
    connect: 'Messaging (SMS, WhatsApp, email)',
    kashy: 'Kashy (online payment)',
    configured: 'Active',
    missing: 'Being activated by Beta Suite',
    account: 'Account',
    suspended: 'Sending suspended for safety',
    suspendedHint:
      'Too many recent sending failures. Check the numbers and templates, then re-enable live sending.',
  },
  templateReview: {
    submit: 'Submit for WhatsApp approval',
    sync: 'Refresh status',
    submitted: 'Template submitted for approval.',
    reviewNote: 'Rejection reason',
  },
  mfa: {
    required:
      'Enable two-factor authentication to access the portal (mandatory for administrators).',
  },
  onboarding: {
    title: 'Agency setup',
    sandbox: 'Demo tenant: no real messages or payments.',
    done: 'done',
    todo: 'to do',
    steps: {
      insurer: 'Create at least one insurer',
      products: 'Create the product lines',
      users: 'Invite the team',
      portfolio: 'Import the portfolio',
      scenario: 'Publish a scenario',
      templates: 'Get the WhatsApp templates approved',
      messaging: 'Messaging activated by Beta Suite',
      live: 'Leave draft mode',
    },
  },
  connectors: {
    tab: 'IS connector',
    intro:
      'Beta Assur reads your information system at regular intervals, read-only: no data is ever written on your side. Each read goes through the same checks as file imports.',
    url: 'Source address',
    urlHint: 'JSON API or published CSV file, over HTTPS.',
    format: 'Format',
    formats: { json: 'JSON API', csv: 'CSV file' },
    interval: 'Read frequency',
    intervals: {
      15: 'Every 15 minutes',
      60: 'Every hour',
      360: 'Every 6 hours',
      1440: 'Once a day',
    },
    auth: 'Access',
    auths: {
      none: 'No authentication',
      bearer: 'Token (Bearer)',
      basic: 'Username and password',
      header: 'Key in a header',
    },
    headerName: 'Header name',
    secret: 'Token or key',
    secretBasic: 'username:password',
    secretHint: 'Stored encrypted, never shown again.',
    secretKept: 'Leave empty to keep the saved access.',
    itemsPath: 'Records path',
    itemsPathHint: 'Empty if the response is an array.',
    sinceParam: '"Modified since" parameter',
    sinceParamHint: 'To read only the changes since the last successful read.',
    create: 'Declare the source',
    save: 'Save',
    edit: 'Edit',
    remove: 'Delete the connector',
    removeMessage: 'Delete this connector? Data already imported is kept.',
    activate: 'Scheduled read active',
    enabled: 'Active',
    disabled: 'Inactive',
    test: 'Test read',
    runNow: 'Read now',
    queued: 'Read scheduled: the result shows up in the log.',
    testResult: (rows: number, status: number) =>
      `Test read (HTTP ${status}): ${rows} record(s), nothing was imported.`,
    saveMapping: 'Save the mapping',
    mappingSaved: 'Mapping saved.',
    missing: 'Incomplete mapping: run a test read and map the required fields.',
    lastRun: 'Last read',
    never: 'Never',
    journal: 'Read log',
    noRuns: 'No read yet.',
    runAt: 'Date',
    trigger: 'Origin',
    triggers: { schedule: 'Scheduled', manual: 'Manual' },
    status: 'Result',
    statuses: { ok: 'Imported', empty: 'Nothing new', error: 'Error' },
    rows: 'Records',
    detail: 'Detail',
    batch: 'View the import',
  },
  developers: {
    title: 'Developers',
    keys: 'API keys',
    keyLabel: 'Label',
    scopes: 'Permissions',
    scopeLabels: {
      read: 'Read',
      'contracts:write': 'Policies',
      'installments:write': 'Installments',
      'payments:write': 'Payments',
    },
    createKey: 'Create key',
    newKey: 'New key: copy it now.',
    newSecret: 'Signing secret: copy it now.',
    shownOnce: 'It will not be shown again. Only its fingerprint is stored.',
    prefix: 'Prefix',
    lastUsed: 'Last call',
    never: 'Never',
    revoked: 'Revoked',
    revoke: 'Revoke',
    revokeMessage: 'Calls using this key will be rejected immediately.',
    example: 'Example call',
    docs: 'Public API documentation:',
    requests: 'Call log',
    webhooks: 'Outgoing webhooks',
    url: 'HTTPS endpoint URL',
    events: 'Events',
    addWebhook: 'Add webhook',
    active: 'Active',
    deleteWebhook: 'No more events will be sent to this URL.',
    deliveries: 'Deliveries',
    deliveryStatuses: {
      pending: 'Pending',
      delivered: 'Delivered',
      failed: 'Retry scheduled',
      undelivered: 'Undelivered',
    },
    attempts: { one: '{n} attempt', other: '{n} attempts' },
    replay: 'Resend',
  },
  reports: {
    title: 'Reports',
    kinds: {
      recovery: 'Collections',
      funnel: 'Funnel',
      scenarios: 'Scenarios',
      channels: 'Channels',
      quality: 'Data quality',
      worklist: 'Worklist',
      consumption: 'Usage',
      preferredChannels: 'Preferred channel',
    },
    summary: 'Overview',
    previous: 'Previous period',
    insurer: 'Insurer',
    periodLabel: 'Period:',
    exportCsv: 'Export to CSV',
    exportXlsx: 'Export to Excel',
    exports: 'List exports',
    exportKinds: {
      installments: 'Installments',
      payments: 'Payments',
      messages: 'Messages',
      policyholders: 'Policyholders',
    },
    monthly: 'Monthly collection reports',
    noMonthly: 'The first monthly report will be available on the 1st of next month.',
    download: 'Download',
  },
};
